const HEIMDALL_EMPLOYEE_TOKEN_KEY = 'tpl_heimdall_employee_token';

/**
 * Decode a JWT payload segment that is base64url-encoded.
 * JWT uses base64url (RFC 4648 §5): `-` instead of `+`, `_` instead of `/`, no padding.
 * Plain `atob` rejects `-` and `_`, causing intermittent failures.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    // Convert base64url → base64, then add padding
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Stored employee pool token for Heimdall admin calls. Only set when the current
 * token is from the employee pool (see main.tsx: NEXT_PUBLIC_COGNITO_EMPLOYEE_POOL_ISSUER).
 * Used so we send the employee token to Heimdall, not the tenant pool token.
 */
export function getEmployeeTokenForHeimdall(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(HEIMDALL_EMPLOYEE_TOKEN_KEY);
}

export function setEmployeeTokenForHeimdall(token: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(HEIMDALL_EMPLOYEE_TOKEN_KEY, token);
}

export function clearEmployeeTokenForHeimdall(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(HEIMDALL_EMPLOYEE_TOKEN_KEY);
}

/**
 * Token used for Heimdall admin API calls (orgs/tenants).
 * Preference order:
 *   1. Employee token stored in sessionStorage (set by main.tsx / csm/tenants page after issuer check).
 *   2. Token from /api/auth/alb-info, but ONLY if its `iss` matches the employee pool issuer
 *      returned by that endpoint. A tenant-pool token is rejected to prevent sending the wrong
 *      token to Heimdall admin endpoints.
 *   3. Returns null — callers should surface an auth error rather than use an unverified token.
 *
 * Token type: Heimdall admin endpoints expect the **access token**; sending the ID token can
 * cause 403 or "User not found or inactive". Callers (main.tsx, csm/page, csm/tenants) store
 * and this helper prefers rawAccessToken when reading from alb-info.
 */
export async function getTokenForHeimdallAdmin(
  _idTokenFallback: string | null
): Promise<string | null> {
  const stored = getEmployeeTokenForHeimdall();
  if (stored && typeof stored === 'string' && stored.trim() !== '') return stored;
  try {
    const res = await fetch('/api/auth/alb-info', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    const employeePoolIssuer = ((data?.employeePoolIssuer as string) ?? '').trim();
    // Prefer access token for Heimdall; ID token can cause 403 on first request.
    const t =
      (data?.tokens?.rawAccessToken as string) ||
      (data?.tokens?.rawIdToken as string);
    if (!t || typeof t !== 'string' || !t.trim()) return null;
    // Only return the token if it is from the employee pool.
    // If employeePoolIssuer is not configured (e.g. local dev without env var), allow the token
    // through so local development still works.
    if (employeePoolIssuer) {
      const payload = decodeJwtPayload(t);
      const iss = ((payload?.iss as string) ?? '').trim();
      if (iss !== employeePoolIssuer) {
        console.warn('[heimdall-admin-token] Rejected non-employee token from alb-info (iss mismatch)');
        return null;
      }
    }
    return t;
  } catch {
    // ignore
  }
  return null;
}
